import axios from "axios";
import { captureUtm, getTrackingContext } from "@/lib/tracking";
import { AUTH_EXPIRED_EVENT, isSessionExpiry } from "@/lib/session";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "mastofa_token";
export const getToken = () => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } };
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const client = axios.create({ baseURL: API });

// A rejected token ends the session for the whole app; credential attempts are excluded.
client.interceptors.response.use((response) => response, (error) => {
  if (isSessionExpiry(error, Boolean(getToken()))) {
    clearToken();
    try { window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT)); } catch { /* non-browser */ }
  }
  return Promise.reject(error);
});

client.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Auth
let attributionCapture = null;
let firstCampaign = null;
export const captureAffiliateAttribution = () => {
  if (!firstCampaign) {
    const query = new URLSearchParams(window.location.search);
    const referral = (query.get("ref") || query.get("referral") || "").trim().toUpperCase();
    const utm = { ...getTrackingContext().attribution, ...captureUtm(window.location.search) };
    const referral_code = /^[A-Z0-9]{6,32}$/.test(referral) ? referral : undefined;
    if (!referral_code && !Object.keys(utm).length) return Promise.resolve({ affiliate_attributed: false });
    firstCampaign = { referral_code, utm };
  }
  if (!attributionCapture) {
    attributionCapture = client.post("/affiliate/capture", firstCampaign, { withCredentials: true })
      .then((r) => r.data).catch((error) => { attributionCapture = null; throw error; });
  }
  return attributionCapture;
};
export const apiRegister = async (payload) => {
  await captureAffiliateAttribution().catch(() => {});
  return client.post("/auth/register", { ...getTrackingContext(), ...payload }, { withCredentials: true }).then((r) => r.data);
};
export const apiLogin = (payload) =>
  client.post("/auth/login", { ...getTrackingContext(), ...payload }).then((r) => r.data);
export const apiMe = () => client.get("/auth/me").then((r) => r.data);
export const apiLogout = () => client.post("/auth/logout").then((r) => r.data);

// Analytics never receives auth credentials or protected business event input.
export const getPublicTrackingConfig = () => client.get("/tracking/config").then((r) => r.data);
export const sendTrackingPageView = (payload) => client.post("/tracking/events", payload).then((r) => r.data);
export const getTrackingEvents = () => client.get("/tracking/events").then((r) => r.data);
export const updateTrackingConsent = (consent) => client.patch("/tracking/consent", { consent }).then((r) => r.data);
export const getAdminTrackingSettings = () => client.get("/admin/tracking/settings").then((r) => r.data);
export const updateAdminTrackingSettings = (payload) => client.patch("/admin/tracking/settings", payload).then((r) => r.data);
export const getAdminTrackingEvents = () => client.get("/admin/tracking/events").then((r) => r.data);
export const getAdminWhatsAppSettings = () => client.get("/admin/whatsapp/settings").then((r) => r.data);
export const updateAdminWhatsAppSettings = (payload) => client.patch("/admin/whatsapp/settings", payload).then((r) => r.data);
export const connectAdminWhatsApp = (payload) => client.post("/admin/whatsapp/connect", payload).then((r) => r.data);
export const disconnectAdminWhatsApp = () => client.post("/admin/whatsapp/disconnect").then((r) => r.data);
export const getAdminWhatsAppNotifications = () => client.get("/admin/whatsapp/notifications").then((r) => r.data);
export const getAffiliateDashboard = () => client.get("/affiliate/dashboard").then((r) => r.data);
export const getAdminAffiliateDashboard = () => client.get("/admin/affiliate/dashboard").then((r) => r.data);
export const updateAdminAffiliateSettings = (payload) => client.patch("/admin/affiliate/settings", payload).then((r) => r.data);
export const setAdminAffiliateCommissionStatus = (id, payload) => client.patch(`/admin/affiliate/commissions/${encodeURIComponent(id)}`, payload).then((r) => r.data);
export const createAdminAffiliatePayout = (payload) => client.post("/admin/affiliate/payouts", payload).then((r) => r.data);
export const payAdminAffiliatePayout = (id, payload) => client.post(`/admin/affiliate/payouts/${encodeURIComponent(id)}/paid`, payload).then((r) => r.data);

// Admin (role-based; server verifies)
export const adminLogin = (payload) => client.post("/admin/login", payload).then((r) => r.data);
export const getAdminOverview = () => client.get("/admin/overview").then((r) => r.data);
export const getAdminUsers = () => client.get("/admin/users").then((r) => r.data);
export const getAdminSalesVideos = () => client.get("/admin/sales-videos").then((r) => r.data);
export const getAdminCreditTransactions = () => client.get("/admin/credit-transactions").then((r) => r.data);
export const getAdminHealth = () => client.get("/admin/health").then((r) => r.data);
export const getVideoModels = () => client.get("/video-models").then((r) => r.data);
export const getAdminVideoModels = () => client.get("/admin/video-models").then((r) => r.data);
export const setAdminVideoModelEnabled = (key, enabled) =>
  client.patch(`/admin/video-models/${encodeURIComponent(key)}/toggle`, { enabled }).then((r) => r.data);
export const setAdminDefaultVideoModel = (key) =>
  client.patch(`/admin/video-models/${encodeURIComponent(key)}/default`).then((r) => r.data);
export const reconcileAdminSalesVideo = (id) =>
  client.post(`/admin/sales-videos/${id}/reconcile`).then((r) => r.data);

export const createVideo = (payload) => client.post("/videos", payload).then((r) => r.data);
export const listVideos = () => client.get("/videos").then((r) => r.data);
export const getVideo = (id) => client.get(`/videos/${id}`).then((r) => r.data);
export const updateVideo = (id, payload) => client.put(`/videos/${id}`, payload).then((r) => r.data);
export const deleteVideo = (id) => client.delete(`/videos/${id}`).then((r) => r.data);
export const getSettingsStatus = () => client.get("/settings/status").then((r) => r.data);

// Model Hub: read-only. The backend lists only models that pass every eligibility gate and never runs a model here.
export const getModelCatalog = (category) =>
  client.get("/model-catalog", { params: category ? { category } : undefined }).then((r) => r.data);
export const getModelPlayground = (model) =>
  client.get("/model-catalog/playground", { params: { model } }).then((r) => r.data);

// Notifikasi: WhatsApp Web (scan QR) per pengguna. Pemilik ditentukan backend dari token; tidak ada user id dikirim dari browser.
export const getWaWebStatus = () => client.get("/wa-web/status").then((r) => r.data);
export const connectWaWeb = () => client.post("/wa-web/connect").then((r) => r.data);
export const getWaWebQr = () => client.get("/wa-web/qr").then((r) => r.data);
export const disconnectWaWeb = () => client.post("/wa-web/disconnect").then((r) => r.data);
export const getNotificationProviders = () => client.get("/notification-providers").then((r) => r.data);
export const setNotificationPreference = (provider) =>
  client.put("/notification-providers/preference", { provider }).then((r) => r.data);

// Keuangan (admin) dan transaksi milik sendiri. Hanya admin berwenang untuk /admin/finance; pemilik data dari token, tanpa user id dari browser.
const financeGet = (path, params) => client.get(`/admin/finance/${path}`, { params }).then((r) => r.data);
export const getFinanceSummary = (start, end) => financeGet("summary", { start, end });
export const getFinanceLedger = (params) => financeGet("ledger", params);
export const getFinanceProviders = (start, end, group_by) => financeGet("providers", { start, end, group_by });
export const getFinanceRanking = (start, end) => financeGet("ranking", { start, end });
export const getFinanceProfitability = (start, end, recognition) => financeGet("profitability", { start, end, recognition });
export const getFinanceSeries = (start, end, granularity) => financeGet("series", { start, end, granularity });
export const getFinanceAudit = (limit) => financeGet("audit", { limit });
export const runFinanceReconciliation = (start, end) => client.post("/admin/finance/reconciliation", { start, end }).then((r) => r.data);
// Returns the blob and the server's file name (it carries "_PARTIAL" when the export covers only part of the data).
export const downloadFinanceExport = (kind, start, end, recognition) =>
  client.get(`/admin/finance/export/${kind}`, { params: { start, end, recognition }, responseType: "blob" }).then((r) => {
    const match = /filename="([A-Za-z0-9_.-]{1,120})"/.exec((r.headers && r.headers["content-disposition"]) || "");
    return { blob: r.data, filename: match ? match[1] : null };
  });
export const recordFinanceFxRate = (body) => client.post("/admin/finance/fx-rates", body).then((r) => r.data);
export const recordFinancePurchase = (body) => client.post("/admin/finance/provider-purchases", body).then((r) => r.data);
export const importFinancePurchases = (rows) => client.post("/admin/finance/provider-purchases/import", { rows }).then((r) => r.data);
export const recordFinanceOperatingCost = (body) => client.post("/admin/finance/operating-costs", body).then((r) => r.data);
export const recordFinanceAdjustment = (body) => client.post("/admin/finance/adjustments", body).then((r) => r.data);
export const getMyTransactions = (limit) => client.get("/finance/my-transactions", { params: { limit } }).then((r) => r.data);
export const getHistory = (params) => client.get("/history", { params }).then((r) => r.data);

// AI Chat: pemilik dari token; nonaktif sampai backend melaporkan enabled=true.
export const getChatStatus = () => client.get("/chat/status").then((r) => r.data);
export const createChatConversation = (model) => client.post("/chat/conversations", { model }).then((r) => r.data);
export const listChatConversations = () => client.get("/chat/conversations").then((r) => r.data);
export const getChatConversation = (id) => client.get(`/chat/conversations/${encodeURIComponent(id)}`).then((r) => r.data);
export const deleteChatConversation = (id) => client.delete(`/chat/conversations/${encodeURIComponent(id)}`).then((r) => r.data);
export const sendChatMessage = (id, text, client_request_id) =>
  client.post(`/chat/conversations/${encodeURIComponent(id)}/messages`, { text, client_request_id }).then((r) => r.data);

// Kontrol model admin terpadu: katalog semua model + keputusan aktif/nonaktif (registry video dikelola lewat /admin/video-models).
export const getAdminModelCatalog = (params) => client.get("/admin/model-catalog", { params }).then((r) => r.data);
export const setAdminCatalogModelEnabled = (model, enabled) => client.patch("/admin/model-catalog/enabled", { model, enabled }).then((r) => r.data);

export const generateScript = (payload) =>
  client.post("/script/generate", payload).then((r) => r.data);

export const analyzeProduct = (file, style, description) => {
  const form = new FormData();
  form.append("file", file);
  form.append("style", style);
  if (description) form.append("description", description);
  return client
    .post("/product/analyze", form, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data);
};

export const scriptFromAngle = (payload) =>
  client.post("/script/from-angle", payload).then((r) => r.data);

export const getAvatars = () => client.get("/avatars").then((r) => r.data);
export const getAvatar = (id) => client.get(`/avatars/${id}`).then((r) => r.data);
export const getVoices = () => client.get("/voices").then((r) => r.data);
export const setAvatarDefaultVoice = (id, payload) =>
  client.patch(`/avatars/${id}/default-voice`, payload).then((r) => r.data);

export const createAvatar = (name, file, clientRequestId) => {
  const form = new FormData();
  form.append("name", name);
  form.append("file", file);
  if (clientRequestId) form.append("client_request_id", clientRequestId);
  return client
    .post("/avatars", form, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data);
};

export const getCreditBalance = () => client.get("/credits/balance").then((r) => r.data);
export const getCreditTransactions = () => client.get("/credits/transactions").then((r) => r.data);

// --- Packages & Payments ---
export const getPackages = () => client.get("/packages").then((r) => r.data);
export const getPaymentMethods = () => client.get("/payment-methods").then((r) => r.data);
export const createPaymentOrder = (payload) => client.post("/payment-orders", { ...getTrackingContext(), ...payload }).then((r) => r.data);
export const listPaymentOrders = () => client.get("/payment-orders").then((r) => r.data);
export const getPaymentOrder = (id) => client.get(`/payment-orders/${id}`).then((r) => r.data);
export const uploadPaymentProof = (id, file) => {
  const form = new FormData();
  form.append("file", file);
  return client.post(`/payment-orders/${id}/proof`, form, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const openPaymentProof = async (id) => {
  // Open on the user gesture so browsers permit the window; fetch with Bearer
  // authentication rather than placing credentials in a navigable URL.
  const preview = window.open("about:blank", "_blank");
  if (!preview) throw new Error("Izinkan jendela baru untuk melihat bukti pembayaran.");
  preview.opener = null;
  try {
    const response = await client.get(`/payment-orders/${id}/proof`, { responseType: "blob" });
    const url = URL.createObjectURL(response.data);
    preview.location.replace(url);
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (error) {
    preview.close();
    throw error;
  }
};

// Admin payments
export const getAdminPaymentSettings = () => client.get("/admin/payment-settings").then((r) => r.data);
export const updateAdminPaymentSettings = (payload) => client.patch("/admin/payment-settings", payload).then((r) => r.data);
export const getAdminPackages = () => client.get("/admin/packages").then((r) => r.data);
export const updateAdminPackage = (code, payload) => client.patch(`/admin/packages/${code}`, payload).then((r) => r.data);
export const getAdminPaymentOrders = () => client.get("/admin/payment-orders").then((r) => r.data);
export const reconcileAdminPaymentOrder = (id) => client.post(`/admin/payment-orders/${id}/reconcile`).then((r) => r.data);
export const approvePaymentOrder = (id) => client.post(`/admin/payment-orders/${id}/approve`).then((r) => r.data);
export const rejectPaymentOrder = (id, reason) => client.post(`/admin/payment-orders/${id}/reject`, { reason }).then((r) => r.data);

// --- Video Jualan (Sales Video) foundation (STEP 3A) ---
export const uploadAsset = (file, kind) => {
  const form = new FormData();
  form.append("file", file);
  form.append("kind", kind);
  return client
    .post("/assets/upload", form, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data);
};
export const createSalesVideo = (payload) => client.post("/sales-videos", { ...getTrackingContext(), ...payload }).then((r) => r.data);
export const listSalesVideos = (params = {}) => client.get("/sales-videos", { params }).then((r) => r.data);
export const getSalesVideo = (id) => client.get(`/sales-videos/${id}`).then((r) => r.data);
export const previewSalesVideo = (id) => client.get(`/sales-videos/${id}/preview`).then((r) => r.data);
export const getSalesVideoTemplates = () => client.get("/sales-video-templates").then((r) => r.data);
export const getAdminSalesVideoTemplates = () => client.get("/admin/sales-video-templates").then((r) => r.data);
export const setAdminSalesVideoTemplateActive = (id, active) => client.patch(`/admin/sales-video-templates/${encodeURIComponent(id)}`, { active }).then((r) => r.data);
export const generateSalesVideo = (id) => client.post(`/sales-videos/${id}/generate`).then((r) => r.data);
export const reconcileSalesVideo = (id) => client.post(`/sales-videos/${id}/reconcile`).then((r) => r.data);
export const getAssetBlob = (id) => client.get(`/assets/${id}`, { responseType: "blob" }).then((r) => r.data);
export const deleteSalesVideo = (id) => client.delete(`/sales-videos/${id}`).then((r) => r.data);
export const generateSalesConcept = (id, payload) =>
  client.post(`/sales-videos/${id}/concept`, payload).then((r) => r.data);
export const updateSalesConcept = (id, payload) =>
  client.patch(`/sales-videos/${id}/concept`, payload).then((r) => r.data);

// ShortDrama shares authenticated assets, model controls and the existing wallet.
export const getShortDramaCapabilities = () => client.get("/shortdramas/capabilities").then((r) => r.data);
export const createShortDrama = (payload) => client.post("/shortdramas", { ...getTrackingContext(), ...payload }).then((r) => r.data);
export const listShortDramas = (params = {}) => client.get("/shortdramas", { params }).then((r) => r.data);
export const getShortDrama = (id) => client.get(`/shortdramas/${id}`).then((r) => r.data);
export const getShortDramaStatus = (id) => client.get(`/shortdramas/${id}/status`).then((r) => r.data);
export const updateShortDrama = (id, payload) => client.patch(`/shortdramas/${id}`, payload).then((r) => r.data);
export const generateShortDramaAngles = (id, revision) => client.post(`/shortdramas/${id}/angles`, { expected_revision: revision }).then((r) => r.data);
export const selectShortDramaAngle = (id, payload) => client.patch(`/shortdramas/${id}/angle`, payload).then((r) => r.data);
export const generateShortDramaStory = (id, revision) => client.post(`/shortdramas/${id}/story`, { expected_revision: revision }).then((r) => r.data);
export const updateShortDramaStoryboard = (id, payload) => client.patch(`/shortdramas/${id}/storyboard`, payload).then((r) => r.data);
export const planShortDramaScene = (id, sceneId, payload) => client.post(`/shortdramas/${id}/scenes/${encodeURIComponent(sceneId)}/plan`, payload).then((r) => r.data);
export const previewShortDrama = (id, sceneId = null) => client.get(`/shortdramas/${id}/preview`, { params: sceneId ? { scene_id: sceneId } : {} }).then((r) => r.data);
export const generateShortDrama = (id, payload) => client.post(`/shortdramas/${id}/generate`, { expected_revision: payload.expected_revision, client_request_id: payload.client_request_id }).then((r) => r.data);
export const generateShortDramaScene = (id, sceneId, payload) => client.post(`/shortdramas/${id}/scenes/${encodeURIComponent(sceneId)}/generate`, { expected_revision: payload.expected_revision, client_request_id: payload.client_request_id }).then((r) => r.data);
export const reconcileShortDrama = (id) => client.post(`/shortdramas/${id}/reconcile`).then((r) => r.data);
export const deleteShortDrama = (id) => client.delete(`/shortdramas/${id}`).then((r) => r.data);
export const getAdminShortDramas = (params = {}) => client.get("/admin/shortdramas", { params }).then((r) => r.data);

export const generateVideo = (payload) =>
  client.post("/video/generate", { ...getTrackingContext(), ...payload }).then((r) => r.data);

export const getVideoStatus = (videoId) =>
  client.get(`/video/${videoId}`).then((r) => r.data);

// Read-only outcome of a submitted intent (no provider call); used to resume after a lost response or reload.
export const getVideoByRequest = (clientRequestId) =>
  client.get(`/video/by-request/${encodeURIComponent(clientRequestId)}`).then((r) => r.data);

export const getHeygenAvatars = () => client.get("/heygen/avatars").then((r) => r.data);
export const getHeygenVoices = () => client.get("/heygen/voices").then((r) => r.data);

// Speaking rate: ~2.5 words per second (~150 wpm) for estimation
export const WORDS_PER_SECOND = 2.5;

export const countWords = (text) =>
  text.trim() === "" ? 0 : text.trim().split(/\s+/).length;

export const estimateDuration = (text) =>
  Math.round(countWords(text) / WORDS_PER_SECOND);

// Refund review (admin only; the server decides everything, the browser sends the decision and a note).
export const listRefundReviews = (status = "", offset = 0) => client.get("/admin/refunds", { params: { ...(status ? { status } : {}), offset } }).then((r) => r.data);
export const decideRefund = (orderId, payload) => client.post(`/admin/refunds/${orderId}/decision`, payload).then((r) => r.data);
export const reconcileRefunds = () => client.post("/admin/refunds/reconcile").then((r) => r.data);
